import React from 'react';
import {
    Gauge as LucideGauge,
    FileText as LucideFileText,
    History as LucideHistory,
    Building2 as LucideBuilding2,
    Ruler as LucideRuler,
    TriangleAlert as LucideTriangleAlert,
    Calculator as LucideCalculator,
} from 'lucide-react';

// ============================================================================
// QuoteMaker Icon System v2 — Direction A (Lucide-default + custom QM-native)
// ----------------------------------------------------------------------------
// Redesign 2026-05-18 (claude/<feature-branch>). Replaces the v1 trade-stamp
// set whose strokes were 3.6-3.9 and whose every glyph shared a square frame.
//
// Wayfinding glyphs (Icons.QM*) now delegate to `lucide-react` for the 7 of
// 8 silhouettes that lucide already covers well. The remaining QM-native
// concept (QMAssistant — "AI quote co-pilot for builders") is the only
// hand-drawn wayfinding glyph. QuoteMakerMark is a separate 1-of-1 brand
// stamp reserved for splash / About / PDF header (NOT wayfinding).
//
// CONTRACT (do not break):
//   - Icons.QMAssistant, Icons.QMDashboard, Icons.QMQuotes, Icons.QMHistory,
//     Icons.QMClients, Icons.QMScope, Icons.QMRisk, Icons.QMCosting,
//     Icons.QuoteMakerMark all still exist as React components accepting
//     `{ size, className, style, accent, active, title, ...rest }`.
//   - Existing call sites (91 QM* + ~1160 generic) keep working unchanged.
//   - Generic glyph wrappers (Icons.Trash, Icons.Plus, ...) still resolve to
//     the underlying lucide-shaped <Icon d="..." /> renderer.
//
// SIGNATURE (the one unforgettable detail per ICON_PRINCIPLES.md §7):
//   The orange focus-dot. Pass `active={true}` to a wayfinding glyph and a
//   4px #EB7232 dot is overlaid at the bottom-right with a one-shot 200ms
//   ease-out pulse on the active->inactive boundary. No looping animation
//   (per DESIGN_LANGUAGE.md motion rules).
//
// STROKES (per ICON_PRINCIPLES.md §2):
//   1.5px default for the generic Icon path renderer (was 2-2.25).
//   1.75px active state (when `active=true` is passed to a wayfinding icon).
//   Custom glyphs use 1.5px and bump to 1.75px when active.
//   NOTHING in this file is allowed to use stroke-width >= 3.5; an automated
//   regression test asserts that contract.
//
// VIEWBOX:
//   24×24 for all wayfinding + generic glyphs. The QuoteMakerMark brand
//   stamp is the only icon allowed to keep the 64×64 viewBox because it's
//   the trade-stamp brand identity, not a navigation element.
// ============================================================================

export const BRAND_ORANGE = 'var(--qm-accent)';

// Wave H (.jsx -> .tsx): type-only annotations. A single permissive IconProps
// shape covers the default Icon renderer, the wayfinding wrapper, the brand
// stamp wrapper, and the generic glyph closures (each spreads ...rest onto an
// SVG / span). The index signature carries the arbitrary forwarded SVG attrs;
// no render-logic, glyph path, viewBox, or stroke value is changed.
export interface IconProps {
    d?: string;
    size?: number;
    className?: string;
    style?: React.CSSProperties;
    strokeWidth?: number;
    accent?: string;
    active?: boolean;
    title?: string;
    variant?: string;
    'aria-hidden'?: boolean | 'true' | 'false';
    // Forwarded SVG/DOM attributes spread via ...rest.
    [key: string]: any;
}

// The internal glyph adapters receive a narrow size/strokeWidth/accent/title set.
interface GlyphProps {
    size?: number;
    strokeWidth?: number;
    accent?: string;
    title?: string;
}

// Generic 24×24 outline path renderer used by Icons.Trash / Plus / etc.
// Default stroke is 1.5 (was 2-2.25); pass strokeWidth explicitly to override.
// Active wayfinding callers pass strokeWidth=1.75.
export default function Icon({
    d,
    size = 20,
    className = '',
    style = {},
    strokeWidth: sw,
    accent, // accepted for backwards-compat; unused on generic glyphs
    ...rest
}: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={sw || 1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            style={style}
            {...rest}
        >
            <path d={d} />
        </svg>
    );
}

// ----------------------------------------------------------------------------
// Wayfinding wrapper. Sits over a lucide (or custom) SVG and overlays the
// orange focus-dot when active=true. The wrapper is a span (not a div) so it
// stays inline-flow inside button rows.
// ----------------------------------------------------------------------------
function WayfindingIcon({
    Glyph,
    size = 20,
    className = '',
    style = {},
    active = false,
    accent, // accepted for back-compat; only used by QMAssistant + QuoteMakerMark
    strokeWidth,
    title,
    'aria-hidden': ariaHidden,
    ...rest
}: IconProps & { Glyph: React.ComponentType<GlyphProps> }) {
    // Active state: bump stroke from 1.5 → 1.75. No bigger weight — colour +
    // dot do the heavy signalling per ICON_PRINCIPLES.md §5-§7.
    const stroke = strokeWidth ?? (active ? 1.75 : 1.5);
    const dotSize = Math.max(4, Math.round(size * 0.22));
    return (
        <span
            className={className}
            style={{
                position: 'relative',
                display: 'inline-flex',
                width: size,
                height: size,
                lineHeight: 0,
                flexShrink: 0,
                ...style,
            }}
            aria-hidden={ariaHidden}
            {...rest}
        >
            <Glyph
                size={size}
                strokeWidth={stroke}
                accent={accent}
                title={title}
            />
            {active ? (
                <span
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        right: 0,
                        bottom: 0,
                        width: dotSize,
                        height: dotSize,
                        borderRadius: '50%',
                        background: BRAND_ORANGE,
                        boxShadow: '0 0 0 1.5px rgba(255,255,255,0.85)',
                        // One-shot ease-out arrival pulse. Not looping per
                        // DESIGN_LANGUAGE.md motion rules.
                        animation: 'qm-icon-focus-dot 200ms ease-out',
                    }}
                />
            ) : null}
        </span>
    );
}

// Lucide-glyph adapter: thin shim that hands lucide the size + stroke we
// computed and forwards an optional title for a11y. Lucide v1.16 accepts
// `size`, `strokeWidth`, `color`, etc. as props.
function makeLucideGlyph(LucideComp: React.ComponentType<any>) {
    return function LucideGlyphAdapter({ size, strokeWidth, title }: GlyphProps) {
        return (
            <LucideComp
                size={size}
                strokeWidth={strokeWidth}
                aria-hidden={title ? undefined : 'true'}
            >
                {title ? <title>{title}</title> : null}
            </LucideComp>
        );
    };
}

const LucideGaugeGlyph = makeLucideGlyph(LucideGauge);
const LucideFileTextGlyph = makeLucideGlyph(LucideFileText);
const LucideHistoryGlyph = makeLucideGlyph(LucideHistory);
const LucideBuilding2Glyph = makeLucideGlyph(LucideBuilding2);
const LucideRulerGlyph = makeLucideGlyph(LucideRuler);
const LucideTriangleAlertGlyph = makeLucideGlyph(LucideTriangleAlert);
const LucideCalculatorGlyph = makeLucideGlyph(LucideCalculator);

// ----------------------------------------------------------------------------
// Custom QMAssistant glyph — the one wayfinding concept off-the-shelf icons
// can't carry. Speech-bubble silhouette with a single sparkle accent at the
// top-right. Sparkle picks up the brand orange via `accent`. NOT a bare
// sparkle (which ICON_PRINCIPLES.md "Specific bans" explicitly forbids).
// ----------------------------------------------------------------------------
function QMAssistantGlyph({ size, strokeWidth = 1.5, accent = BRAND_ORANGE, title }: GlyphProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            focusable="false"
            aria-hidden={title ? undefined : 'true'}
        >
            {title ? <title>{title}</title> : null}
            {/* Speech bubble — gentle rounded square with a tail at bottom-left. */}
            <path d="M5 4h11a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H10l-4 3v-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            {/* Conversation flow — single hairline inside the bubble suggesting
                a thread of dialogue, not the "two dots = stale message" idiom
                of generic chat apps. Reads as "in conversation". */}
            <path d="M7.5 8.5h6 M7.5 12.5h9" strokeWidth={strokeWidth} />
            {/* 4-ray sparkle accent at top-right — a true bursting sparkle, not
                a "+" math glyph. Four short rays from one centre point; the
                centre is implicit (the rays converge but don't intersect). The
                accent colour distinguishes the sparkle from the silhouette. */}
            <path
                d="M19 3.5v2.4 M19 7.3v1.4 M16.7 5.2h2 M19.3 5.2h1.6 M17.4 3.8l1.2 1 M19.6 6.6l1.2 1"
                stroke={accent}
                strokeWidth={Math.max(1.5, strokeWidth)}
                strokeLinecap="round"
            />
        </svg>
    );
}

// ----------------------------------------------------------------------------
// QuoteMakerMark — the 1-of-1 brand stamp. NOT a wayfinding glyph. Used for
// LoginPage splash, About card, PDF header. Keeps the trade-stamp 64×64
// viewBox because the stamp IS the identity here. Strokes capped at 2.25
// (heavy accent permitted per ICON_PRINCIPLES.md §2; nothing >= 3.5).
// ----------------------------------------------------------------------------
function QuoteMakerMarkGlyph({ size, strokeWidth, accent = BRAND_ORANGE, title }: GlyphProps) {
    const stamp = strokeWidth ?? 2.25;
    const stripe = 1.5;
    const innerLetter = 1.75;
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 64 64"
            fill="none"
            focusable="false"
            aria-hidden={title ? undefined : 'true'}
        >
            {title ? <title>{title}</title> : null}
            {/* Outer trade-stamp frame — slightly inset from the canvas. */}
            <rect
                x="9"
                y="9"
                width="46"
                height="46"
                rx="3"
                stroke="currentColor"
                strokeWidth={stamp}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {/* Q monogram — circle + the diagonal tail breaking out at 4 o'clock,
                a deliberate echo of the wax-seal stamp visual idiom. */}
            <circle
                cx="32"
                cy="32"
                r="12"
                stroke="currentColor"
                strokeWidth={stamp}
                fill="none"
            />
            <path
                d="M39 39l7 7"
                stroke="currentColor"
                strokeWidth={stamp}
                strokeLinecap="round"
            />
            {/* Inner "M" stroke nested inside the Q's bowl — the QuoteMaker
                wordmark compressed into a single typographic gesture. Three
                strokes form a slim M: two verticals at 27 + 37, a single V
                between them. The mark thus encodes "QM" — Q outside, M inside —
                in one symbol, which is what gives the stamp its identity. */}
            <path
                d="M27 27v10 M37 27v10 M27 27l5 6 5-6"
                stroke="currentColor"
                strokeWidth={innerLetter}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {/* Two hairline accent stripes — the trade-stamp ink convention.
                One at top-left (under the frame edge) and one at bottom-right
                (above the frame edge), framing the monogram. Drawn in accent
                colour, hairline weight per ICON_PRINCIPLES.md §5 (one ink
                mode of accent, one hairline). */}
            <path
                d="M14 14h12 M38 50h12"
                stroke={accent}
                strokeWidth={stripe}
                strokeLinecap="round"
            />
        </svg>
    );
}

// ----------------------------------------------------------------------------
// Public exports — both the legacy named component (for back-compat with
// callers that import it directly) and the unified Icons map.
// ----------------------------------------------------------------------------

// Variant → glyph map. Direction A: 7 lucide + 1 custom (assistant).
const WAYFINDING_GLYPHS: Record<string, React.ComponentType<GlyphProps>> = {
    assistant: QMAssistantGlyph,
    dashboard: LucideGaugeGlyph,
    quotes: LucideFileTextGlyph,
    history: LucideHistoryGlyph,
    clients: LucideBuilding2Glyph,
    scope: LucideRulerGlyph,
    risk: LucideTriangleAlertGlyph,
    costing: LucideCalculatorGlyph,
};

// Legacy named component — preserved because a handful of files import it
// directly (StageSurfaceHeader / PortalChromeHeader / FullQuoteEditorModal
// pass it via `icon={...}` props). Now resolves to the wayfinding wrapper.
export function QuoteMakerTradeStampIcon({
    variant = 'assistant',
    size = 20,
    className = '',
    style = {},
    strokeWidth,
    accent = BRAND_ORANGE,
    active = false,
    title,
    ...rest
}: IconProps) {
    const Glyph = WAYFINDING_GLYPHS[variant!] || WAYFINDING_GLYPHS.assistant!;
    return (
        <WayfindingIcon
            Glyph={Glyph}
            size={size}
            className={className}
            style={style}
            strokeWidth={strokeWidth}
            accent={accent}
            active={active}
            title={title}
            {...rest}
        />
    );
}

// Helper that builds the 8 wayfinding wrappers in one go, keeping the
// public surface mechanical and grep-able.
const makeWayfinding = (variant: string) => (p: IconProps) => (
    <QuoteMakerTradeStampIcon variant={variant} {...p} />
);

// Brand stamp — gets its OWN wrapper so it doesn't accidentally share the
// `quotes` wayfinding silhouette (this was an actual bug in v1).
function QuoteMakerMarkComponent({
    size = 20,
    className = '',
    style = {},
    strokeWidth,
    accent = BRAND_ORANGE,
    title,
    ...rest
}: IconProps) {
    return (
        <span
            className={className}
            style={{
                position: 'relative',
                display: 'inline-flex',
                width: size,
                height: size,
                lineHeight: 0,
                flexShrink: 0,
                ...style,
            }}
            {...rest}
        >
            <QuoteMakerMarkGlyph
                size={size}
                strokeWidth={strokeWidth}
                accent={accent}
                title={title}
            />
        </span>
    );
}

// Typed as Record<string, any> (NOT ComponentType<IconProps>) on purpose: the
// ~20 consumer files render `<Icons.X />` and were written against the prior
// untyped (.jsx) export. Imposing a precise component type here would surface
// new JSX-callability errors in every consumer (a blast-radius change outside
// this leaf slice). The component *parameters* below still carry real types
// (IconProps / GlyphProps); only the map's public surface stays permissive.
export const Icons: Record<string, any> = {
    // --- Wayfinding (8 variants, formerly the trade-stamp set) -------------
    QMAssistant: makeWayfinding('assistant'),
    QMDashboard: makeWayfinding('dashboard'),
    QMQuotes: makeWayfinding('quotes'),
    QMHistory: makeWayfinding('history'),
    QMClients: makeWayfinding('clients'),
    QMScope: makeWayfinding('scope'),
    QMRisk: makeWayfinding('risk'),
    QMCosting: makeWayfinding('costing'),
    // --- Brand identity (1-of-1, NOT a wayfinding glyph) -------------------
    QuoteMakerMark: QuoteMakerMarkComponent,
    // --- Generic UI glyphs (lucide-shaped paths, 1.5px stroke) -------------
    Clipboard: (p: IconProps) => <Icon d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M8 2h8v4H8z" {...p} />,
    Shield: (p: IconProps) => <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" {...p} />,
    Clock: (p: IconProps) => <Icon d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 6v6l4 2" {...p} />,
    Info: (p: IconProps) => <Icon d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 16v-4 M12 8h.01" {...p} />,
    CheckCircle: (p: IconProps) => <Icon d="M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4L12 14.01l-3-3" {...p} />,
    ArrowRight: (p: IconProps) => <Icon d="M5 12h14 M12 5l7 7-7 7" {...p} />,
    List: (p: IconProps) => <Icon d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" {...p} />,
    File: (p: IconProps) => <Icon d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M13 2v7h7" {...p} />,
    FileText: (p: IconProps) => <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" {...p} />,
    Upload: (p: IconProps) => <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12" {...p} />,
    Image: (p: IconProps) => <Icon d="M3 3h18v18H3z M8.5 8.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21" {...p} />,
    ImagePlus: (p: IconProps) => <Icon d="M3 3h18v18H3z M8.5 8.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21 M16 3v6 M13 6h6" {...p} />,
    Images: (p: IconProps) => <Icon d="M18 22H4a2 2 0 0 1-2-2V6 M8 2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M22 13l-4-4-6 8" {...p} />,
    X: (p: IconProps) => <Icon d="M18 6L6 18M6 6l12 12" {...p} />,
    LogOut: (p: IconProps) => <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9" {...p} />,
    Check: (p: IconProps) => <Icon d="M20 6L9 17l-5-5" {...p} />,
    AlertTriangle: (p: IconProps) => <Icon d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z M12 9v4 M12 17h.01" {...p} />,
    ChevronRight: (p: IconProps) => <Icon d="M9 18l6-6-6-6" {...p} />,
    ChevronLeft: (p: IconProps) => <Icon d="M15 18l-6-6 6-6" {...p} />,
    ChevronDown: (p: IconProps) => <Icon d="M6 9l6 6 6-6" {...p} />,
    ChevronUp: (p: IconProps) => <Icon d="M18 15l-6-6-6 6" {...p} />,
    Settings: (p: IconProps) => <Icon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" {...p} />,
    Trash: (p: IconProps) => <Icon d="M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" {...p} />,
    Plus: (p: IconProps) => <Icon d="M12 5v14 M5 12h14" {...p} />,
    Minus: (p: IconProps) => <Icon d="M5 12h14" {...p} />,
    Sparkles: (p: IconProps) => <Icon d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z M5 3v4 M19 17v4 M3 5h4 M17 19h4" {...p} />,
    Printer: (p: IconProps) => <Icon d="M6 9V2h12v7 M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2 M6 14h12v8H6z" {...p} />,
    Mail: (p: IconProps) => <Icon d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z M22 6l-10 7L2 6" {...p} />,
    Download: (p: IconProps) => <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3" {...p} />,
    History: (p: IconProps) => <Icon d="M12 8v4l3 3 M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8 M3 3v5h5" {...p} />,
    Search: (p: IconProps) => <Icon d="M11 17.25a6.25 6.25 0 1 1 0-12.5 6.25 6.25 0 0 1 0 12.5z M16 16l4.5 4.5" {...p} />,
    ExternalLink: (p: IconProps) => <Icon d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6 M15 3h6v6 M10 14L21 3" {...p} />,
    DollarSign: (p: IconProps) => <Icon d="M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" {...p} />,
    User: (p: IconProps) => <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" {...p} />,
    Users: (p: IconProps) => <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75" {...p} />,
    Building: (p: IconProps) => <Icon d="M3 21h18 M5 21V7l8-4v18 M19 21V11l-6-4 M9 9v.01 M9 12v.01 M9 15v.01 M9 18v.01" {...p} />,
    Phone: (p: IconProps) => <Icon d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" {...p} />,
    Droplet: (p: IconProps) => <Icon d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" {...p} />,
    Tool: (p: IconProps) => <Icon d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" {...p} />,
    Wrench: (p: IconProps) => <Icon d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" {...p} />,
    Home: (p: IconProps) => <Icon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10" {...p} />,
    Layers: (p: IconProps) => <Icon d="M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5" {...p} />,
    Grid: (p: IconProps) => <Icon d="M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z" {...p} />,
    Copy: (p: IconProps) => <Icon d="M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" {...p} />,
    Calculator: (p: IconProps) => <Icon d="M4 2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z M8 6h8 M8 10h8 M8 14h2 M8 18h2 M14 14h2 M14 18h2" {...p} />,
    Percent: (p: IconProps) => <Icon d="M19 5L5 19 M6.5 5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z M17.5 16a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" {...p} />,
    Zap: (p: IconProps) => <Icon d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" {...p} />,
    Eye: (p: IconProps) => <Icon d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" {...p} />,
    EyeOff: (p: IconProps) => <Icon d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24 M1 1l22 22" {...p} />,
    Filter: (p: IconProps) => <Icon d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" {...p} />,
    Tag: (p: IconProps) => <Icon d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z M7 7h.01" {...p} />,
    Move: (p: IconProps) => <Icon d="M5 9l-3 3 3 3 M9 5l3-3 3 3 M15 19l-3 3-3-3 M19 9l3 3-3 3 M2 12h20 M12 2v20" {...p} />,
    Maximize2: (p: IconProps) => <Icon d="M15 3h6v6 M9 21H3v-6 M21 3l-7 7 M3 21l7-7" {...p} />,
    Minimize2: (p: IconProps) => <Icon d="M4 14h6v6 M20 10h-6V4 M14 10l7-7 M3 21l7-7" {...p} />,
    PanelLeft: (p: IconProps) => <Icon d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M9 3v18" {...p} />,
    PanelRight: (p: IconProps) => <Icon d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M15 3v18" {...p} />,
    // Six dots, each a radius-1 ring under the 1.5 stroke, so it paints as a
    // solid dot about 2px across at 14px. They were zero-length strokes under
    // 1px wide, and the grip read as a blank area (Peter, 2026-10-14).
    GripVertical: (p: IconProps) => <Icon d="M8 5a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M8 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M8 19a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M14 5a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M14 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M14 19a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" {...p} />,
    BarChart: (p: IconProps) => <Icon d="M12 20V10 M6 20V14 M18 20V4" {...p} />,
    TrendingUp: (p: IconProps) => <Icon d="M23 6l-9.5 9.5-5-5L1 18 M17 6h6v6" {...p} />,
    ArrowLeft: (p: IconProps) => <Icon d="M19 12H5 M12 19l-7-7 7-7" {...p} />,
    Save: (p: IconProps) => <Icon d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8" {...p} />,
    Lock: (p: IconProps) => <Icon d="M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z M7 11V7a5 5 0 0 1 10 0v4" {...p} />,
    Lightbulb: (p: IconProps) => <Icon d="M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.9V17h8v-2.1A7 7 0 0 0 12 2z" {...p} />,
    LayoutDashboard: (p: IconProps) => <Icon d="M3 3h7v7H3z M14 3h7v9h-7z M3 14h7v7H3z M14 16h7v5h-7z" {...p} />,
    Menu: (p: IconProps) => <Icon d="M3 12h18 M3 6h18 M3 18h18" {...p} />,
    Folder: (p: IconProps) => <Icon d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" {...p} />,
    FolderOpen: (p: IconProps) => <Icon d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z M2 10h20" {...p} />,
    FolderPlus: (p: IconProps) => <Icon d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z M12 11v6 M9 14h6" {...p} />,
    Loader2: (p: IconProps) => <Icon d="M21 12a9 9 0 1 1-6.219-8.56" {...p} />,
    Brain: (p: IconProps) => <Icon d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4 M8.5 16a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z M15.5 16a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z" {...p} />,
    MessageSquare: (p: IconProps) => <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" {...p} />,
    MessageCircle: (p: IconProps) => <Icon d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" {...p} />,
    ToggleLeft: (p: IconProps) => <Icon d="M16 5H8a5 5 0 0 0 0 10h8a5 5 0 0 0 0-10z M8 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" {...p} />,
    ToggleRight: (p: IconProps) => <Icon d="M16 5H8a5 5 0 0 0 0 10h8A5 5 0 0 0 16 5z M16 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" {...p} />,
    Trash2: (p: IconProps) => <Icon d="M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6" {...p} />,
    MapPin: (p: IconProps) => <Icon d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" {...p} />,
    Calendar: (p: IconProps) => <Icon d="M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" {...p} />,
    Send: (p: IconProps) => <Icon d="M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z" {...p} />,
    RefreshCw: (p: IconProps) => <Icon d="M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0 1 14.85-3.36L23 10 M1 14l4.64 4.36A9 9 0 0 0 20.49 15" {...p} />,
    Globe: (p: IconProps) => <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" {...p} />,
    Monitor: (p: IconProps) => <Icon d="M2 3h20v14H2z M8 21h8 M12 17v4" {...p} />,
    Paperclip: (p: IconProps) => <Icon d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49" {...p} />,
    RotateCcw: (p: IconProps) => <Icon d="M1 4v6h6 M3.51 15a9 9 0 1 0 2.13-9.36L1 10" {...p} />,
    Mic: (p: IconProps) => <Icon d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z M19 10v2a7 7 0 0 1-14 0v-2 M12 19v4 M8 23h8" {...p} />,
    MicOff: (p: IconProps) => <Icon d="M1 1l22 22 M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6 M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .27-.02.54-.05.8 M12 19v4 M8 23h8" {...p} />,
    Video: (p: IconProps) => <Icon d="M23 7l-7 5 7 5V7z M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" {...p} />,
    Play: (p: IconProps) => <Icon d="M5 3l14 9-14 9V3z" {...p} />,
    Pause: (p: IconProps) => <Icon d="M6 4h4v16H6z M14 4h4v16h-4z" {...p} />,
    Volume2: (p: IconProps) => <Icon d="M11 5L6 9H2v6h4l5 4V5z M19.07 4.93a10 10 0 0 1 0 14.14 M15.54 8.46a5 5 0 0 1 0 7.07" {...p} />,
    VolumeX: (p: IconProps) => <Icon d="M11 5L6 9H2v6h4l5 4V5z M23 9l-6 6 M17 9l6 6" {...p} />,
    ArrowUp: (p: IconProps) => <Icon d="M12 19V5 M5 12l7-7 7 7" {...p} />,
    Bot: (p: IconProps) => <Icon d="M5 11h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z M12 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4z M12 7v4 M8 16h.01 M16 16h.01" {...p} />,
    Edit: (p: IconProps) => <Icon d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" {...p} />,
    Database: (p: IconProps) => <Icon d="M12 2C6.48 2 2 4.02 2 6.5v11C2 19.98 6.48 22 12 22s10-2.02 10-4.5v-11C22 4.02 17.52 2 12 2z M2 6.5C2 8.98 6.48 11 12 11s10-2.02 10-4.5 M2 12c0 2.48 4.48 4.5 10 4.5s10-2.02 10-4.5" {...p} />,
    Highlighter: (p: IconProps) => <Icon d="M9 11l-6 6v3h3l6-6 M10.5 9.5l4-4 4 4-4 4 M15.5 4.5l3 3" {...p} />,
    Eraser: (p: IconProps) => <Icon d="M7 21h10 M5.5 13L14 4.5a2.12 2.12 0 1 1 3 3L8.5 16 M3.5 15l3 3" {...p} />,
    Type: (p: IconProps) => <Icon d="M4 7V4h16v3 M9 20h6 M12 4v16" {...p} />,
    Target: (p: IconProps) => <Icon d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12z M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" {...p} />,
    Undo: (p: IconProps) => <Icon d="M3 7v6h6 M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6.36 2.64L3 13" {...p} />,
    Redo: (p: IconProps) => <Icon d="M21 7v6h-6 M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6.36 2.64L21 13" {...p} />,
    Pencil: (p: IconProps) => <Icon d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z M15 5l4 4" {...p} />,
    ListOrdered: (p: IconProps) => <Icon d="M10 6h11 M10 12h11 M10 18h11 M4 6h1v4 M4 10h2 M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" {...p} />,
    AlertCircle: (p: IconProps) => <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 8v4 M12 16h.01" {...p} />,
    BarChart2: (p: IconProps) => <Icon d="M18 20V10 M12 20V4 M6 20v-6" {...p} />,
    CheckSquare: (p: IconProps) => <Icon d="M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" {...p} />,
    Award: (p: IconProps) => <Icon d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12z M8.21 13.89L7 23l5-3 5 3-1.21-9.12" {...p} />,
    Key: (p: IconProps) => <Icon d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" {...p} />,
    ClipboardCheck: (p: IconProps) => <Icon d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z M9 14l2 2 4-4" {...p} />,
    XCircle: (p: IconProps) => <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M15 9l-6 6 M9 9l6 6" {...p} />,
    Star: (p: IconProps) => <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" {...p} />,
    Circle: (p: IconProps) => <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z" {...p} />,
    Polygon: (p: IconProps) => <Icon d="M12 2.5L22 9.5l-3.82 12H5.82L2 9.5z M12 2.5v0 M22 9.5v0 M18.18 21.5v0 M5.82 21.5v0 M2 9.5v0" {...p} />,
    AlignLeft: (p: IconProps) => <Icon d="M17 10H3 M21 6H3 M21 14H3 M17 18H3" {...p} />,
    Square: (p: IconProps) => <Icon d="M3 3h18v18H3z" {...p} />,
    Code: (p: IconProps) => <Icon d="M16 18l6-6-6-6 M8 6l-6 6 6 6" {...p} />,
    Activity: (p: IconProps) => <Icon d="M22 12h-4l-3 9L9 3l-3 9H2" {...p} />,
    Edit3: (p: IconProps) => <Icon d="M12 20h9 M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" {...p} />,
    Loader: (p: IconProps) => <Icon d="M21 12a9 9 0 1 1-6.219-8.56" {...p} />,
    ClipboardList: (p: IconProps) => <Icon d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M8 2h8v4H8z M12 11h4 M12 16h4 M8 11h.01 M8 16h.01" {...p} />,
    Bell: (p: IconProps) => <Icon d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0" {...p} />,
    Heart: (p: IconProps) => <Icon d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" {...p} />,
    AlertOctagon: (p: IconProps) => <Icon d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2z M12 8v4 M12 16h.01" {...p} />,
    ShieldAlert: (p: IconProps) => <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M12 8v4 M12 16h.01" {...p} />,
    ArrowUpRight: (p: IconProps) => <Icon d="M7 17L17 7 M7 7h10v10" {...p} />,
    // View mode icons - card view (stacked cards) vs table view (horizontal rows)
    LayoutCards: (p: IconProps) => <Icon d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z M3 15a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4z" {...p} />,
    LayoutTable: (p: IconProps) => <Icon d="M3 3h18v18H3z M3 9h18 M3 15h18 M9 3v18" {...p} />,
    // Charts + misc
    PieChart: (p: IconProps) => <Icon d="M21.21 15.89A10 10 0 1 1 8 2.83 M22 12A10 10 0 0 0 12 2v10z" {...p} />,
    TrendingDown: (p: IconProps) => <Icon d="M23 18l-9.5-9.5-5 5L1 6" {...p} />,
    HelpCircle: (p: IconProps) => <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3 M12 17h.01" {...p} />,
    // Three-dot overflow ("more"): used for compressed action menus.
    MoreHorizontal: (p: IconProps) => <Icon d="M5 12h.01 M12 12h.01 M19 12h.01" {...p} />,
};
